import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Calendar, ExternalLink, MessageCircle, Loader2, BadgeDollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import { chatService } from '@/lib/api/services/chat.service';
import { toast } from 'sonner';
import type { InterestedFreelancer } from '@/lib/api/services/certification.service';

interface InterestedFreelancerCardProps {
  freelancer: InterestedFreelancer;
}

function formatRate(cents: number | null): string | null {
  if (!cents) return null;
  return `$${Math.round(cents / 100)}/hr`;
}

export function InterestedFreelancerCard({ freelancer }: InterestedFreelancerCardProps) {
  const navigate = useNavigate();
  const [isStartingChat, setIsStartingChat] = useState(false);
  const profile = freelancer.freelancer_profiles;
  const initials = profile.display_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??';

  const rate = formatRate(profile.hourly_rate_cents);

  const handleStartChat = async () => {
    if (!profile.user_id) {
      toast.error('Unable to start chat — user ID not available');
      return;
    }
    setIsStartingChat(true);
    try {
      const conversation = await chatService.createOrFindConversation(profile.user_id);
      navigate(`/genius/chat`, { state: { conversationId: conversation.id } });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to start chat');
    } finally {
      setIsStartingChat(false);
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow h-[320px] overflow-hidden">
      <CardContent className="pt-5 pb-4 h-full flex flex-col">
        {/* Header: Avatar + Name + Meta */}
        <div className="flex items-start gap-3">
          <Avatar className="h-14 w-14 flex-shrink-0 border-2 border-slate-100">
            <AvatarImage src={profile.profile_picture_url || undefined} alt={profile.display_name} />
            <AvatarFallback className="bg-color-1/10 text-color-1 font-bold text-lg">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base truncate">{profile.display_name}</h3>
            <p className="text-xs text-muted-foreground truncate">
              {profile.headline || 'AI Expert'}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
              {profile.location && (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate max-w-[100px]">{profile.location}</span>
                </span>
              )}
              {rate && (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <BadgeDollarSign className="h-3 w-3 flex-shrink-0" />
                  {rate}
                </span>
              )}
              {freelancer.timezone && (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Clock className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate max-w-[80px]">{freelancer.timezone}</span>
                </span>
              )}
              {freelancer.preferred_schedule && (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Calendar className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate max-w-[80px]">{freelancer.preferred_schedule}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Skills */}
        <div className="flex flex-wrap gap-1 mt-3 min-h-[24px]">
          {profile.skills && (profile.skills as string[]).length > 0 ? (
            <>
              {(profile.skills as string[]).slice(0, 5).map((skill: string) => (
                <Badge key={skill} variant="secondary" className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 border-0">
                  {skill}
                </Badge>
              ))}
              {(profile.skills as string[]).length > 5 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  +{(profile.skills as string[]).length - 5}
                </Badge>
              )}
            </>
          ) : (
            <span className="text-[10px] text-muted-foreground/50 italic">No skills listed yet</span>
          )}
        </div>

        {/* Bio or Note */}
        <div className="mt-2 flex-1 min-h-0">
          {freelancer.note ? (
            <p className="text-xs text-muted-foreground line-clamp-2 italic">
              &ldquo;{freelancer.note}&rdquo;
            </p>
          ) : profile.bio ? (
            <p className="text-xs text-muted-foreground line-clamp-2">
              {(profile.bio as string).substring(0, 120)}...
            </p>
          ) : (
            <p className="text-xs text-muted-foreground/30 italic">No bio yet</p>
          )}
        </div>

        {/* Buttons — pushed to bottom */}
        <div className="mt-auto pt-3 flex gap-2">
          <Button
            variant="default"
            size="sm"
            className="flex-1 text-xs"
            onClick={handleStartChat}
            disabled={isStartingChat}
          >
            {isStartingChat ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <MessageCircle className="h-3 w-3 mr-1" />
            )}
            {isStartingChat ? 'Starting...' : 'Message'}
          </Button>
          <Link to={`/genius/${profile.id}`} className="flex-1">
            <Button variant="outline" size="sm" className="w-full text-xs">
              <ExternalLink className="h-3 w-3 mr-1" />
              Profile
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
